import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Spinner } from "../../components/States";
import { TimeAgo, fmtMoney } from "../../lib/format";

// Suggest a listing that is not in the directory yet. Nothing here publishes:
// a submission is reviewed by staff first, which is why the form says so.

const CHAINS = ["ethereum", "base", "arbitrum", "optimism", "polygon"];

type Submission = { id: string; type: string; slug: string; name: string; status: string; createdAt: string };
type Options = Awaited<ReturnType<typeof api.submissionOptions>>;

// Mirrors validateSubmission() in src/worker/services/submissions.ts. The
// server stays the authority — this exists so a mistake is pointed at while
// someone is still looking at the field, instead of coming back as a single
// red line after they have pressed submit and lost the context.
const EVM_CHAINS = new Set(CHAINS);

function validUrl(v: string): boolean {
  try {
    const u = new URL(v.trim());
    return (u.protocol === "https:" || u.protocol === "http:") && !!u.hostname.includes(".");
  } catch {
    return false;
  }
}

interface Field {
  key: "name" | "website" | "shortDesc" | "symbol" | "contractAddress";
  label: string;
  error: string | null;
}

function problems(f: {
  type: "token" | "company";
  name: string;
  symbol: string;
  website: string;
  shortDesc: string;
  chain: string;
  contractAddress: string;
}): Field[] {
  const out: Field[] = [];
  const name = f.name.trim();
  out.push({
    key: "name",
    label: "Name",
    error: !name
      ? "Required."
      : name.length < 2 || name.length > 80
        ? "Must be 2–80 characters."
        : !/[a-z0-9]/i.test(name)
          ? "Must contain letters or numbers."
          : null,
  });
  out.push({
    key: "website",
    label: "Official website",
    error: !f.website.trim()
      ? "Required."
      : validUrl(f.website)
        ? null
        : "Enter a full URL, including https://",
  });
  const desc = f.shortDesc.trim();
  out.push({
    key: "shortDesc",
    label: "What it does",
    error: !desc
      ? "Required."
      : desc.length < 20
        ? `${20 - desc.length} more character${20 - desc.length === 1 ? "" : "s"} needed.`
        : desc.length > 200
          ? "Must be 200 characters or fewer."
          : null,
  });
  out.push({
    key: "symbol",
    label: "Symbol",
    error:
      f.type === "token" && f.symbol && !/^[A-Za-z0-9.$-]{1,12}$/.test(f.symbol)
        ? "Letters, numbers, dot, dollar or dash. Up to 12."
        : null,
  });
  out.push({
    key: "contractAddress",
    label: "Contract address",
    error:
      f.type === "token" && f.contractAddress
        ? !/^0x[0-9a-fA-F]{40}$/.test(f.contractAddress.trim())
          ? "Must be 0x followed by 40 hex characters."
          : !EVM_CHAINS.has(f.chain)
            ? "Choose the chain this contract is on."
            : null
        : null,
  });
  return out;
}

function StatusChip({ status }: { status: string }) {
  const label =
    status === "published" ? "Live" : status === "pending" ? "Awaiting review" : "Not accepted";
  return <span className={`chip chip-${status}`}>{label}</span>;
}

export function Submit() {
  const [type, setType] = useState<"token" | "company">("token");
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [website, setWebsite] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [chain, setChain] = useState("ethereum");
  const [contractAddress, setContractAddress] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; slug?: string; type?: string } | null>(null);
  const [done, setDone] = useState(false);
  const [mine, setMine] = useState<Submission[] | null>(null);
  const [options, setOptions] = useState<Options | null>(null);
  const [priority, setPriority] = useState(false);
  const [managedNotes, setManagedNotes] = useState("");
  // A field complains once you have left it, or once you have tried to submit.
  // Shouting at someone for an empty box they have not reached yet is noise.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [tried, setTried] = useState(false);

  // The two types can be priced differently, so the figure has to follow the
  // radio the submitter has selected rather than being read once on mount.
  const priceCents = options ? options.prices[type] : null;
  const priceLabel = priceCents != null ? fmtMoney(priceCents, options!.currency) : null;

  const fields = problems({ type, name, symbol, website, shortDesc, chain, contractAddress });
  const errorFor = (key: Field["key"]) => {
    const f = fields.find((x) => x.key === key);
    return f && f.error && (tried || touched[key]) ? f.error : null;
  };
  const blur = (key: Field["key"]) => () => setTouched((t) => ({ ...t, [key]: true }));
  const missing = fields.filter((f) => f.error);
  const closed = !!options && !options.freeAllowed && !options.priorityAvailable;

  const load = () =>
    api
      .mySubmissions()
      .then((r) => setMine(r.items))
      .catch(() => setMine([]));

  useEffect(() => {
    load();
    api
      .submissionOptions()
      .then((o) => {
        setOptions(o);
        // When free submission is closed, paid is the only route — preselect it
        // rather than letting someone fill the form and be refused at the end.
        if (!o.freeAllowed && o.priorityAvailable) setPriority(true);
      })
      .catch(() => setOptions(null));
  }, []);

  const submit = async () => {
    setTried(true);
    // Caught here rather than round-tripping: the server rejects the same
    // things, but its answer arrives as one line at the top of a form the
    // person has already scrolled away from.
    if (missing.length > 0) {
      const first = document.querySelector<HTMLElement>(`[data-field="${missing[0]!.key}"]`);
      first?.scrollIntoView({ block: "center", behavior: "smooth" });
      first?.focus({ preventScroll: true });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.submitListing({
        type,
        name,
        symbol: symbol || null,
        website,
        shortDesc,
        chain: type === "token" && contractAddress ? chain : null,
        contractAddress: type === "token" ? contractAddress || null : null,
        priority,
      });
      // A paid submission is created first and paid second, so an abandoned
      // checkout leaves the listing in the free lane rather than losing it.
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }
      setDone(true);
      setTried(false);
      setTouched({});
      setName("");
      setSymbol("");
      setWebsite("");
      setShortDesc("");
      setContractAddress("");
      load();
    } catch (err) {
      const e = err as { body?: { error?: { message?: string; slug?: string; type?: string } } };
      setError({
        message: e.body?.error?.message ?? "Something went wrong. Please try again.",
        slug: e.body?.error?.slug,
        type: e.body?.error?.type,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-24">
      <section className="card pad-card submit-card">
        <h2>Suggest a listing</h2>
        <p className="muted submit-lede">
          Missing a token or company? Add it here. A moderator checks every submission before it
          appears in the directory, so it will not be live straight away.
        </p>

        {done && !error && (
          <p className="notice notice-ok">
            Thanks — your submission is with our moderators. You can track it below.
          </p>
        )}
        {error && (
          <p className="notice notice-error">
            {error.message}{" "}
            {error.slug && error.type && (
              <Link to={`/${error.type}/${error.slug}`}>View the existing listing</Link>
            )}
          </p>
        )}

        {closed && (
          <p className="notice notice-error">
            Listing submissions are closed at the moment. Payment is required here and the
            payment route is not available, so nothing you submit could be accepted. Please try
            again later.
          </p>
        )}

        <div className="submit-layout">
          <div className="grid gap-12 form-grid">
            {/* Grouped, because "what is it / where is it / what does it do" is
                the order someone actually knows the answers in. One flat column
                of eight boxes gives no sense of how much is left. */}
            <fieldset className="field-group">
              <legend>What are you adding?</legend>

              <label>
                <span>Type</span>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as "token" | "company")}
                >
                  <option value="token">A token or coin</option>
                  <option value="company">A company, exchange, wallet or protocol</option>
                </select>
              </label>

              <div className={type === "token" ? "field-row field-row-split" : "field-row"}>
                <label>
                  <span>Name</span>
                  <input
                    data-field="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={blur("name")}
                    placeholder="Acme Protocol"
                    maxLength={80}
                    aria-invalid={!!errorFor("name")}
                  />
                  {errorFor("name") && <small className="field-error">{errorFor("name")}</small>}
                </label>

                {type === "token" && (
                  <label>
                    <span>Symbol <span className="muted">(optional)</span></span>
                    <input
                      data-field="symbol"
                      value={symbol}
                      onChange={(e) => setSymbol(e.target.value)}
                      onBlur={blur("symbol")}
                      placeholder="ACME"
                      maxLength={12}
                      aria-invalid={!!errorFor("symbol")}
                    />
                    {errorFor("symbol") && (
                      <small className="field-error">{errorFor("symbol")}</small>
                    )}
                  </label>
                )}
              </div>
            </fieldset>

            <fieldset className="field-group">
              <legend>Where can people find it?</legend>

              <label>
                <span>Official website</span>
                <input
                  data-field="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  onBlur={blur("website")}
                  placeholder="https://acme.org"
                  inputMode="url"
                  spellCheck={false}
                  aria-invalid={!!errorFor("website")}
                />
                {errorFor("website") ? (
                  <small className="field-error">{errorFor("website")}</small>
                ) : (
                  <small className="muted">The project&rsquo;s own site, not a listing on another directory.</small>
                )}
              </label>

              {type === "token" && (
                <div className="field-row field-row-chain">
                  <label>
                    <span>Chain</span>
                    <select value={chain} onChange={(e) => setChain(e.target.value)}>
                      {CHAINS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>
                      Contract address <span className="muted">(optional)</span>
                    </span>
                    <input
                      data-field="contractAddress"
                      value={contractAddress}
                      onChange={(e) => setContractAddress(e.target.value)}
                      onBlur={blur("contractAddress")}
                      placeholder="0x…"
                      spellCheck={false}
                      className="mono"
                      aria-invalid={!!errorFor("contractAddress")}
                    />
                    {errorFor("contractAddress") ? (
                      <small className="field-error">{errorFor("contractAddress")}</small>
                    ) : /^0x[0-9a-fA-F]{40}$/.test(contractAddress.trim()) ? (
                      <small className="field-ok">Looks like a valid address.</small>
                    ) : (
                      <small className="muted">
                        This is what lets a reader check they are looking at the right token, and
                        it unlocks the automated security scan. Paste it carefully.
                      </small>
                    )}
                  </label>
                </div>
              )}
            </fieldset>

            <fieldset className="field-group">
              <legend>What does it do?</legend>
              <label>
                <span className="sr-only-label">Description</span>
                <textarea
                  data-field="shortDesc"
                  value={shortDesc}
                  onChange={(e) => setShortDesc(e.target.value)}
                  onBlur={blur("shortDesc")}
                  rows={3}
                  maxLength={200}
                  placeholder="One or two plain sentences a stranger would understand."
                  aria-invalid={!!errorFor("shortDesc")}
                />
                <small className="field-foot">
                  {errorFor("shortDesc") ? (
                    <span className="field-error">{errorFor("shortDesc")}</span>
                  ) : (
                    <span className="muted">Plain language. No marketing claims.</span>
                  )}
                  <span
                    className={
                      shortDesc.trim().length > 0 && shortDesc.trim().length < 20
                        ? "field-count field-count-low"
                        : "field-count muted"
                    }
                  >
                    {shortDesc.trim().length}/200
                  </span>
                </small>
              </label>
            </fieldset>

            {options && !options.freeAllowed && options.priorityAvailable && (
              <p className="notice notice-error">
                Listings on this site are paid{priceLabel ? ` — ${priceLabel}` : ""}. You will be
                sent to Stripe after you submit, and refunded in full if the listing is not
                accepted.
              </p>
            )}

            {options?.priorityAvailable && (
              <div className="pay-choice">
                <label className="pay-option">
                  <input
                    type="radio"
                    name="review-speed"
                    checked={!priority}
                    disabled={!options.freeAllowed}
                    onChange={() => setPriority(false)}
                  />
                  <span>
                    <b>Free review</b>
                    <small>
                      {options.freeAllowed
                        ? "Reviewed when a moderator gets to it."
                        : "Not available on this site."}
                    </small>
                  </span>
                </label>
                <label className="pay-option">
                  <input
                    type="radio"
                    name="review-speed"
                    checked={priority}
                    onChange={() => setPriority(true)}
                  />
                  <span>
                    <b>
                      {options.freeAllowed ? "Priority review" : "Listing review"}
                      {priceLabel && <span className="pay-price">{priceLabel}</span>}
                    </b>
                    <small>
                      A decision within {options.slaHours} hours. It buys a decision, not an
                      approval — if we reject it, you are refunded.
                    </small>
                  </span>
                </label>
              </div>
            )}

            <div className="submit-actions">
              <button className="btn btn-primary" onClick={submit} disabled={busy || closed}>
                {busy
                  ? "Submitting…"
                  : priority
                    ? priceLabel
                      ? `Continue to payment · ${priceLabel}`
                      : "Continue to payment"
                    : "Submit for review"}
              </button>
              {/* A dead button that will not say why is the worst version of
                  this. The button stays live and the form explains itself. */}
              {missing.length > 0 && (tried || Object.keys(touched).length > 0) && (
                <span className="muted submit-missing">
                  Still needed: {missing.map((f) => f.label.toLowerCase()).join(", ")}
                </span>
              )}
            </div>
          </div>

          {/* The right-hand column was empty space on every desktop screen.
              What belongs there is the answer to "what happens after I press
              this", which is the question the form itself raises. */}
          <aside className="submit-aside">
            <h3>What happens next</h3>
            <ol className="submit-steps">
              <li>
                <b>A moderator reads it</b>
                <span>
                  {options?.paidOffered && priority
                    ? `A decision within ${options.slaHours} hours.`
                    : "Usually within a few days."}
                </span>
              </li>
              <li>
                <b>We check the facts</b>
                <span>
                  The website, and the contract on a chain scanner if you gave one. Anything we
                  cannot verify is removed rather than published.
                </span>
              </li>
              <li>
                <b>It goes live, or it does not</b>
                <span>
                  Either way you will see the outcome under “Your submissions”. Nothing is
                  published without a person looking at it.
                </span>
              </li>
            </ol>

            <h3>What gets accepted</h3>
            <ul className="submit-tips">
              <li>A real, reachable official website.</li>
              <li>A description that says what the thing does, not why it is great.</li>
              <li>For a token, the contract address — it is optional, but a listing without one can never carry a security scan.</li>
            </ul>

            <p className="submit-note muted">
              Adding a listing does not give it a rating. Ratings come from reviews, and only
              from reviews.
            </p>
          </aside>
        </div>
      </section>

      {options?.managedAvailable && (
        <section className="card pad-card">
          <h3>Would you rather we did it?</h3>
          <p className="muted">
            We build the profile for you: check the contract on-chain, write the description,
            gather your official links, run the security scan and set the categories. You get a
            finished, verified listing to claim.
          </p>
          <div className="grid gap-12 form-grid">
            <label>
              <span>What should we list? <span className="muted">(name, website, contract)</span></span>
              <textarea
                rows={3}
                maxLength={2000}
                value={managedNotes}
                onChange={(e) => setManagedNotes(e.target.value)}
                placeholder="Acme Protocol — https://acme.org — 0x…"
              />
            </label>
            <div>
              <button
                className="btn btn-ghost"
                disabled={busy || managedNotes.trim().length < 5}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const res = await api.requestManagedOnboarding(managedNotes);
                    if (res.checkoutUrl) window.location.href = res.checkoutUrl;
                  } catch {
                    setError({ message: "Could not start that request. Please try again." });
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Request managed onboarding
              </button>
            </div>
          </div>
        </section>
      )}

      <section>
        <h3>Your submissions</h3>
        {mine === null ? (
          <Spinner />
        ) : mine.length === 0 ? (
          <p className="muted">Nothing submitted yet.</p>
        ) : (
          <ul className="list-plain grid gap-8">
            {mine.map((s) => (
              <li key={s.id} className="card pad-card row space-between">
                <span>
                  {s.status === "published" ? (
                    <Link to={`/${s.type}/${s.slug}`}>{s.name}</Link>
                  ) : (
                    s.name
                  )}{" "}
                  <span className="muted">· submitted <TimeAgo iso={s.createdAt} /></span>
                </span>
                <StatusChip status={s.status} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
